import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import {
  getUserStatusStyle,
  USER_STATUS_ACTIVE,
  USER_STATUS_FILTER_OPTIONS,
} from "@/v2-backoffice-client/user/status/userStatus.constants"
import { useSearchParams } from "@/v2-backoffice-client/utils"
import type { TableQueryParams } from "@/v2-backoffice-client/components/table"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import { DotText } from "@/v2-backoffice-client/components/dot"
import Table, {
  createColumns,
  useTableQuery,
  GROUPING_RANGE_DATES,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import { useUserQuery } from "@/v2-backoffice-client/user/user.query"
import { useCustomerQuery } from "@/v2-backoffice-client/customer/customer.query"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"
import type { UserStatusFragment } from "@/v2-backoffice-client/user/status/__types__/user.status.fragment"
import type { UserStatusCountsQuery } from "@/v2-backoffice-client/user/counts/__types__/user.counts.query"
import UserTableSelectionToolbar from "@/v2-backoffice-client/user/UserTableSelectionToolbar"
import UserTableRowToolbar from "@/v2-backoffice-client/user/UserTableRowToolbar"
import UserActivityBadge from "@/v2-backoffice-client/user/activity/UserActivityBadge"
import UserStatusBadge from "@/v2-backoffice-client/user/status/UserStatusBadge"
import UserFlaggedBadge from "@/v2-backoffice-client/user/flagged/UserFlaggedBadge"
import UserIdentityCell from "@/v2-backoffice-client/user/identity/UserIdentityCell"
import { getIdentityStatusConfig } from "@/v2-backoffice-client/user/identity/userIdentity.constants"
import UserContactSyncCell from "@/v2-backoffice-client/user/contact/UserContactSyncCell"
import {
  CONTACT_SYNC_STATUS_FILTER_OPTIONS,
  getContactSyncStatusLabel,
} from "@/v2-backoffice-client/user/contact/userContact.constants"
import UserMigrationCell from "@/v2-backoffice-client/user/migration/UserMigrationCell"
import {
  ACTIVATION_STATUS_FILTER_OPTIONS,
  getActivationStatusLabel,
} from "@/v2-backoffice-client/tenant/migration/tenantMigration.constants"
import {
  useLicenseGoverningStatuses,
  type LicenseStatusFilterOption,
} from "@/v2-backoffice-client/license/license.statuses.hooks"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import TruncatedList from "@/v2-backoffice-client/components/truncatedList"
import { buildUserUsageColumns } from "@/v2-backoffice-client/usage/usage.columns"
import { buildUserLicenseColumns } from "@/v2-backoffice-client/user/user.columns"

const PAGE_SIZE = 100
const COLUMN_SIZING = {
  Id: 50,
  Name: 175,
  UserStatusCode: 90,
  UserActivityLevelCode: 80,
  // Email: 200,
  TenantNames: 150,
  CreatedAt: 130,
  IdentityCreatedAt: 130,
  LastSignInAt: 130,
  LastSuccessfulSignInAt: 130,
  ContactSyncStatus: 120,
  MigrationStatus: 130,
  LicenseStatusCode: 150,
  LicensePlanNames: 200,
}

const COUNTS_BY_CODE_KEYS: Record<
  number,
  keyof Omit<UserStatusCountsQuery["getUserStatusCounts"], "__typename" | "all">
> = {
  1: "provisioned",
  2: "active",
  3: "blocked",
  4: "removed",
}

// Sentinel pill value for the "Flagged" tab. It is not a UserStatusCode
// (those are 1-4): the tab filters the orthogonal IsFlagged column instead,
// via the option's filterColumn override, so flagged users show regardless
// of status.
const FLAGGED_PILL_VALUE = 99

/**
 * The status pill's server filter for the table's FIRST query, resolved
 * from the same URL param Table.StatusFilter persists to — in exactly the
 * shape buildQueryParams emits once the pill mounts, so Apollo sees
 * deep-equal variables and never fires a wasted unfiltered query on load.
 * No param means the default Active pill.
 */
function buildUserStatusParams(statusParam: string | null): TableQueryParams {
  if (statusParam === String(FLAGGED_PILL_VALUE)) {
    return { filters: [{ column: "IsFlagged", eq: "true" }] }
  }
  if (statusParam !== null && /^\d+$/.test(statusParam)) {
    return { filters: [{ column: "UserStatusCode", eq: statusParam }] }
  }
  return {
    filters: [{ column: "UserStatusCode", eq: String(USER_STATUS_ACTIVE) }],
  }
}

type CreateUserColumnsInput = {
  /** Customer-name options for the CustomerNames advanced filter. */
  customerOptions: { label: string; value: string }[]
  /** License-status options (statuses + "No license") for the License filter, as codes: the board filters server-side. */
  licenseStatusOptions: LicenseStatusFilterOption[]
  /** Status display names from the getLicenseStatuses lookup. */
  licenseStatusNamesByCode: Map<number, string>
}

function createUserColumns(input: CreateUserColumnsInput) {
  const { customerOptions, licenseStatusOptions, licenseStatusNamesByCode } =
    input
  return createColumns<UserFragment>({
    enableSelection: true,
    enableSorting: true,
    enableHiding: true,
    columns: {
      Name: {
        id: "Name",
        label: "User",
        // Server-side: the composite Name entry in usersFilterRegistry
        // (contains matches either part or the full name; is/is-not compare
        // the rendered full name).
        filter: { type: "text", placeholder: "Name..." },
        getValue: (row) => formatUserName(row.FirstName, row.LastName),
        to: ({ row }) => `?v=user:${row.original.Id}`,
      },
      UserStatusCode: {
        id: "UserStatusCode",
        label: "Status",
        enableGrouping: true,
        // The pills cover the common single-status cuts; this filter exists
        // for advanced-tree combos (e.g. exclude removed AND blocked).
        filter: {
          type: "multi-select",
          placeholder: "Any status",
          options: USER_STATUS_FILTER_OPTIONS,
          operators: ["EQ", "NEQ", "IN"],
        },
        renderCell({ row }) {
          return <UserStatusBadge status={row.original.UserStatus} />
        },
        exportValue({ row }) {
          return row.original.UserStatus.Name
        },
      },
      UserActivityLevelCode: {
        id: "UserActivityLevelCode",
        label: "Activity",
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any activity",
          options: [
            { label: "Daily", value: "1" },
            { label: "Weekly", value: "2" },
            { label: "Monthly", value: "3" },
            { label: "Inactive", value: "4" },
            { label: "Unknown", value: "5" },
          ],
          operators: ["EQ", "NEQ", "IN"],
        },
        renderCell({ row }) {
          return <UserActivityBadge level={row.original.UserActivityLevel} />
        },
        exportValue({ row }) {
          return row.original.UserActivityLevel.Name
        },
      },
      IsFlagged: {
        id: "IsFlagged",
        label: "Flagged",
        defaultSize: 90,
        enableGrouping: true,
        // Server-side: the sharingFlag APPLY in Get_Acc_Users — same
        // definition as the row badge and the Flagged pill.
        filter: {
          type: "select",
          placeholder: "Flagged?",
          options: [
            { label: "Flagged", value: "1" },
            { label: "Not flagged", value: "0" },
          ],
          operators: ["EQ"],
        },
        getValue: (row) => (row.IsFlagged ? "Flagged" : ""),
        renderCell({ row }) {
          if (!row.original.IsFlagged) return null
          return <UserFlaggedBadge />
        },
        exportValue({ row }) {
          return row.original.IsFlagged ? "Flagged" : ""
        },
      },
      IdentitySyncStatus: {
        id: "IdentitySyncStatus",
        label: "Identity",
        defaultSize: 120,
        enableGrouping: true,
        // Only out-of-sync / name-drift rows carry a label, so grouping and
        // export read blank for healthy accounts (matching the badge, which
        // renders nothing for them).
        getValue: (row) =>
          getIdentityStatusConfig(row.IdentitySyncStatus)?.label ??
          (row.IdentityNameMismatch ? "Name differs" : ""),
        renderCell({ row }) {
          return <UserIdentityCell user={row.original} />
        },
        exportValue({ row }) {
          return (
            getIdentityStatusConfig(row.original.IdentitySyncStatus)?.label ??
            (row.original.IdentityNameMismatch ? "Name differs" : "")
          )
        },
      },
      // The FUS-927 migration target group: users with v2 evidence (console
      // session or usage snapshot). The wave selection filter: V2 user =
      // Yes + Fusion Login = Not migrated → select all → send.
      UsesV2: {
        id: "UsesV2",
        label: "V2 User",
        defaultSize: 90,
        enableGrouping: true,
        // Server-side: the UsesV2 entry in usersFilterRegistry resolves the
        // same predicate in SQL.
        filter: {
          type: "select",
          placeholder: "V2 usage",
          options: [
            { label: "V2 user", value: "1" },
            { label: "Never used v2", value: "0" },
          ],
          operators: ["EQ", "NEQ", "IN"],
        },
        getValue: (row) => (row.UsesV2 ? "Yes" : "No"),
        renderCell({ row }) {
          return (
            <DotText variant={row.original.UsesV2 ? "success-1" : "muted"}>
              {row.original.UsesV2 ? "Yes" : "No"}
            </DotText>
          )
        },
        exportValue({ row }) {
          return row.original.UsesV2 ? "Yes" : "No"
        },
      },
      // Fusion-login migration state (FUS-927 CS-managed waves).
      MigrationStatus: {
        id: "MigrationStatus",
        label: "Fusion Login",
        defaultSize: 130,
        enableGrouping: true,
        // Server-side: the MigrationStatus entry in usersFilterRegistry
        // (backoffice-api) computes the same classification in SQL.
        filter: {
          type: "multi-select",
          placeholder: "Any migration status",
          options: ACTIVATION_STATUS_FILTER_OPTIONS,
          operators: ["EQ", "NEQ", "IN"],
        },
        getValue: (row) => getActivationStatusLabel(row.MigrationStatus),
        renderCell({ row }) {
          return <UserMigrationCell user={row.original} />
        },
        exportValue({ row }) {
          return getActivationStatusLabel(row.original.MigrationStatus)
        },
      },
      ContactSyncStatus: {
        id: "ContactSyncStatus",
        label: "HubSpot",
        defaultSize: 120,
        enableGrouping: true,
        // Server-side: the ContactSyncStatus entry in usersFilterRegistry
        // (backoffice-api) computes the same classification in SQL.
        filter: {
          type: "multi-select",
          placeholder: "Any HubSpot status",
          options: CONTACT_SYNC_STATUS_FILTER_OPTIONS,
          operators: ["EQ", "NEQ", "IN"],
        },
        getValue: (row) => getContactSyncStatusLabel(row.ContactSyncStatus),
        renderCell({ row }) {
          return <UserContactSyncCell user={row.original} showHealthy />
        },
        exportValue({ row }) {
          return getContactSyncStatusLabel(row.original.ContactSyncStatus)
        },
      },
      Email: {
        id: "Email",
        label: "Email",
        filter: { type: "text", placeholder: "Email..." },
        to: ({ row }) => `?v=user:${row.original.Id}`,
      },
      TenantNames: {
        id: "TenantNames",
        label: "Team(s)",
        enableGrouping: true,
        // Multi-valued relation: no meaningful single sort key, and the
        // server (Get_Acc_Users) has no sort expression for it — without
        // this the header would offer a sort that silently does nothing.
        enableSorting: false,
        // Server-side: EXISTS over the membership join (usersFilterRegistry)
        // — a match on any single team hits.
        filter: {
          type: "text",
          placeholder: "Team name...",
          operators: ["CONTAINS", "EQ"],
        },
        getValue: (row) => row.TenantNames?.[0] ?? "",
        renderCell({ row }) {
          return (
            <TruncatedList
              items={row.original.Tenants.map((tenant) => ({
                label: tenant.Name,
                to: `?v=tenant:${tenant.Id}`,
              }))}
            />
          )
        },
        exportValue({ row }) {
          return row.original.TenantNames?.join(", ") ?? ""
        },
      },
      CustomerNames: {
        id: "CustomerNames",
        label: "Customer",
        description:
          "The customer that owns the user: whose licenses and seats apply to them. Normally the customer of the user's teams; Update Customer on the user moves them.",
        enableGrouping: true,
        // Resolved per row by a dataloader, not a sortable column.
        enableSorting: false,
        getValue: (row) => row.CustomerNames?.[0] ?? "",
        // Server-side: filtering this column reads the same owner FK the
        // cell shows (usersFilterRegistry in the backoffice-api). Options
        // are the known customer names.
        filter: {
          type: "multi-select",
          placeholder: "Customer",
          options: customerOptions,
          operators: ["EQ", "IN", "CONTAINS"],
        },
        renderCell({ row }) {
          return (
            <TruncatedList
              items={row.original.Customers.map((customer) => ({
                label: customer.Name,
                to: `?v=customer:${customer.ExternalCustomerId}`,
              }))}
            />
          )
        },
        exportValue({ row }) {
          return row.original.CustomerNames?.join(", ") ?? ""
        },
      },
      // License posture of the user's owning customer. Filtering and the
      // status-rank sort run server-side against the userLicense APPLY in
      // Get_Acc_Users (usersFilterRegistry), so they match the badge; the
      // Licenses text filter matches any single held license/product name
      // there too. The seats a person holds (the Seats column of the
      // customer and team tables) need a per-user read this board has no
      // proc for yet.
      ...buildUserLicenseColumns<UserFragment>({
        getUser: (row) => row,
        statusNamesByCode: licenseStatusNamesByCode,
        statusOptions: licenseStatusOptions,
        isServerFiltered: true,
      }),
      // Deep link into the user panel's Usage tab so staff can dig into
      // the signal behind the badge.
      ...buildUserUsageColumns<UserFragment>({
        getUsage: (row) => row,
        buildTo: (row) => `?v=user:${row.Id}&user-view-tabs-tab=usage`,
      }),
      LatestAppVersion: {
        id: "LatestAppVersion",
        label: "Latest Version",
        defaultSize: 110,
        enableGrouping: true,
        filter: { type: "text", placeholder: "Version..." },
        getValue: (row) => row.LatestAppVersion ?? "",
        renderCell({ row }) {
          if (!row.original.LatestAppVersion) return null
          return (
            <span className="font-mono text-xs">
              {row.original.LatestAppVersion}
            </span>
          )
        },
        exportValue({ row }) {
          return row.original.LatestAppVersion ?? ""
        },
      },
      LastSignInAt: {
        id: "LastSignInAt",
        label: "Last Sign-in",
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          if (!row.original.LastSignInAt) return null
          return <RelativeDate date={row.original.LastSignInAt} />
        },
        exportValue({ row }) {
          return formatDateString(row.original.LastSignInAt)
        },
      },
      LastSuccessfulSignInAt: {
        id: "LastSuccessfulSignInAt",
        label: "Last Successful Sign-in",
        defaultHidden: true,
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          return <RelativeDate date={row.original.LastSuccessfulSignInAt} />
        },
        exportValue({ row }) {
          return formatDateString(row.original.LastSuccessfulSignInAt)
        },
      },
      IdentityCreatedAt: {
        id: "IdentityCreatedAt",
        label: "Sign-up",
        defaultHidden: true,
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          return <RelativeDate date={row.original.IdentityCreatedAt} />
        },
        exportValue({ row }) {
          return formatDateString(row.original.IdentityCreatedAt)
        },
      },
      CreatedAt: {
        id: "CreatedAt",
        label: "Created",
        defaultHidden: true,
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          return <RelativeDate date={row.original.CreatedAt} />
        },
        exportValue({ row }) {
          return formatDateString(row.original.CreatedAt)
        },
      },
      Id: { id: "Id", label: "Id", defaultSize: 50, defaultHidden: true },
    },
  })
}

export type UserTableProps = Omit<
  TableProps<UserFragment>,
  "data" | "columns"
> & {
  counts?: UserStatusCountsQuery["getUserStatusCounts"]
  statuses?: UserStatusFragment[]
  statusLoading?: boolean
}

function UserTable(props: UserTableProps) {
  const { className, counts, statuses, statusLoading, ...tableProps } = props

  const opener = useAppViewSheetOpener()
  const [searchParams] = useSearchParams()

  // Customer-name options for the CustomerNames advanced filter. Loaded
  // once; filtering itself happens server-side via the EXISTS join.
  const { data: customerData } = useCustomerQuery({
    variables: { input: { limit: PAGE_SIZE } },
  })
  const customerOptions = useMemo(() => {
    const names = new Set<string>()
    for (const customer of customerData?.getCustomers.items ?? []) {
      if (customer.Name) names.add(customer.Name)
    }
    return [...names]
      .toSorted((a, b) => a.localeCompare(b))
      .map((name) => ({ label: name, value: name }))
  }, [customerData])

  // License statuses for the License filter options and badge names: the
  // codes, since the board filters server-side.
  const { namesByCode: licenseStatusNamesByCode, codeOptions } =
    useLicenseGoverningStatuses()

  const columns = useMemo(
    () =>
      createUserColumns({
        customerOptions,
        licenseStatusOptions: codeOptions,
        licenseStatusNamesByCode,
      }),
    [customerOptions, codeOptions, licenseStatusNamesByCode],
  )
  const {
    items,
    pageInfo,
    loading,
    error,
    refetch,
    onFetchMore,
    onQueryChange,
  } = useTableQuery({
    pageSize: PAGE_SIZE,
    useQuery: useUserQuery,
    select: (data) => data.getUsers,
    initialParams: buildUserStatusParams(searchParams.get("status")),
  })

  return (
    <Table
      className={cn("user-table", className)}
      tableId="user-table"
      data={items}
      columns={columns}
      initialColumnSizing={COLUMN_SIZING}
      enableGrouping
      enableRowSelection
      manualSorting
      manualFiltering
      searchColumn="Search"
      onQueryChange={onQueryChange}
      getHighlightedRowId={(row) => {
        return opener.getIsActive("user", row.original.Id)
      }}
      {...tableProps}
    >
      <Table.Toolbar>
        <Table.StatusFilter
          column="UserStatusCode"
          defaultValue={USER_STATUS_ACTIVE}
          isLoading={statusLoading}
        >
          <Table.StatusFilter.Option value={null} count={counts?.all}>
            All
          </Table.StatusFilter.Option>

          {statuses?.map((s) => {
            const style = getUserStatusStyle(s.Code)
            return (
              <Table.StatusFilter.Option
                key={s.Code}
                value={s.Code}
                count={counts?.[COUNTS_BY_CODE_KEYS[s.Code]]}
                iconSrc={style.iconSrc}
                iconClassName={style.iconColor}
              >
                {s.Name}
              </Table.StatusFilter.Option>
            )
          })}

          <Table.StatusFilter.Option
            value={FLAGGED_PILL_VALUE}
            filterColumn="IsFlagged"
            filterValue="true"
            iconSrc="flag"
            count={counts?.flagged}
            title="Flagged for single active session (account-sharing enforcement): these users can only be signed in on one device at a time. Click to show only flagged users."
          >
            Flagged
          </Table.StatusFilter.Option>
        </Table.StatusFilter>

        <div className="flex-1" />
        <Table.Search placeholder="Search by name or email..." />

        <Table.ToolbarFilters />
        <Separator orientation="vertical" />

        <Table.ToolbarMenu onRefresh={refetch} exportFileName="users" />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />
      <Table.Content
        countLabel="users"
        isLoading={loading}
        error={error}
        pageInfo={pageInfo}
        onFetchMore={onFetchMore}
      />

      <Table.RowToolbar<UserFragment> column="Name">
        {(user) => <UserTableRowToolbar user={user} />}
      </Table.RowToolbar>

      <Table.SelectionToolbar<UserFragment>>
        {(users, reset) => {
          return <UserTableSelectionToolbar users={users} reset={reset} />
        }}
      </Table.SelectionToolbar>
    </Table>
  )
}

export default UserTable
